import { ActivityLogResponseDto } from '../commonTypes/ActivityLogTypes/activityLog.types';
import { Evaluation, EvaluationScoreLabel, EvaluationStatus } from '../commonTypes/evaluation/calculate.types';
import { LeaveRequestStatus } from '../commonTypes/leaveRequest.types';
import { NotificationResponseDto, NotificationSettingsDto } from '../commonTypes/NotificationTypes/notification.types';
import { Page } from '../commonTypes/pageable.types';
import {
    PatchUserDto,
    ChangeUserRoleDto,
    PublicUserDto,
    UserDto,
    PublicUserWithoutPoliciesAndRoleDto,
    UserProfileWithRoleDto,
    PolicyDto,
    PublicUserWithBirthday,
    GoogleIntegration,
    PatchGoogleIntegration,
    UserDocumentDto,
    UserDocumentPresignedDto,
    PublicUserWithAnniversary,
    PaginatedPublicUserDto,
    RecalculatePoliciesAmountForUserDTO,
    Category,
    CategoryDto,
    PaginatedUserDocumentsDto,
    MedicalLeaveRequestBaseResponseDto,
} from '../commonTypes/user.types';
import { AppConfig } from '../config/AppConfig';
import { diContainer } from '../libs/container';
import { ApiBaseClass, IServiceHttp } from './ApiBase';

export namespace UserApiRequest {
    export interface RemoveUserFromOrganization {
        params: {
            userSub: string;
        };
    }

    export interface User {
        params: {
            userSub: string;
        };
    }

    export interface UserData {
        body: PatchUserDto;
    }

    export interface UserDataById {
        params: {
            userSub: string;
        };
        body: PatchUserDto;
    }

    export interface UsersBulk {
        body: {
            userSubs: string[];
        };
    }

    export interface GetUsersByRole {
        body: {
            roleNames: string[];
        };
    }

    export interface UsersBulkByRoles {
        body: {
            userSubs: string[];
            roleNames: string[];
        };
    }

    export interface JoinOrganzation {
        params: {
            joinToken: string;
        };
    }

    export interface ChangePolicyInitialAmount {
        params: {
            userSub: string;
            leaveType: string;
        };
        body: {
            newInitialAmount: number;
        };
    }

    export interface RoleData {
        body: ChangeUserRoleDto;
    }

    export interface ProfileImage {
        body: FormData;
    }

    export interface GetAffectedMembersOfPolicy {
        body: {
            leaveType: string;
        };
    }

    export interface GetOrganizationMembersCount {
        body: {
            from: string;
            to: string;
        };
    }

    export interface GetColleaguesBirthdays {
        query: {
            from: string;
            to: string;
        };
    }

    export interface GetAnniversaries {
        query: {
            from: string;
            to: string;
        };
    }

    export interface ConnectToGoogleCalendar {
        body: {
            code: string;
        };
    }

    export interface EmptyRequest {}

    export interface GoogleCalendarEnable {
        body: {
            rejectionReason?: string;
        };
    }

    export interface GoogleCalendarDisable {
        body: {
            rejectionReason?: string;
        };
    }

    export interface GoogleIntegrationPatch {
        body: PatchGoogleIntegration;
    }

    export interface GetUserDocuments {
        params: { userSub: string };
    }

    export interface GetUserDocument {
        params: { id: string };
        query: { presign: boolean };
    }

    export interface ChangeBranch {
        params: {
            userSub: string;
        };
        body: {
            branchId: string;
        };
    }

    export interface UploadUserDocument {
        name: string;
        userSub: string;
        file: File;
        categoryId: string;
        description?: string;
        accessToken: string | null;
        idToken: string | null;
    }

    export interface DeleteUserDocument {
        params: { id: string };
    }

    export interface GetActivityLogRequest {
        params: {
            userSub: string;
        };
    }

    export interface GetNotificationSettings {
        params: {
            userSub: string;
        };
    }

    export interface EnableDisableNotification {
        params: {
            name: string;
        };
    }

    export interface RecalculatePoliciesAmountForUser {
        body: RecalculatePoliciesAmountForUserDTO;
    }

    export interface GetNotificationRequest {
        params: {
            id: string;
        };
    }

    export interface CreateCategory {
        body: CategoryDto;
    }

    export interface PatchCategory {
        params: {
            id: string;
        };
        body: {
            name?: string;
            notificationOn?: boolean;
            description?: string;
            markedAsObsolete?: boolean;
        };
    }
    export interface PatchDocument {
        params: {
            userSub: string;
        };
        query: {
            documentId: string;
        };
        body: {
            name?: string;
            categoryId?: string;
            description?: string;
        };
    }
    export interface PatchSlackChannels {
        body: {
            id: string;
            name?: string;
            isActive?: boolean;
        }[];
    }
    export interface TeamsIntegrationSelectTeam {
        body: {
            teamId: string;
            teamName: string;
        };
    }
    export interface TeamsIntegrationProvisionChannels {
        body: {
            absentChannelName: string;
            celebrationChannelName: string;
        };
    }
    export interface TeamsIntegrationBotInstall {
        body?: {
            activity: unknown;
        };
    }
    export interface TeamsIntegrationCallback {
        query: Record<string, string>;
    }
    export interface GetEvaluationById {
        params: {
            id: string;
        };
    }
    export interface GetAllEvaluations {
        query: {
            status?: string;
            sortBy?: string;
            sortDirection?: 'ASC' | 'DESC';
            page?: number;
            pageSize?: number;
        };
    }

    export interface RequestEvaluation {
        body: {
            evaluatorUserSub: string;
            evaluateeUserSubs: string[];
            evaluatorToEvaluateeRoleId: string;
            dueDate: string;
            isAnonymous?: boolean;
            isNoteRequired?: boolean;
            topicIds: string[];
        };
    }

    export interface GetTopicsByCategoryName {
        query: {
            categoryName: string;
        };
    }

    export interface GetEvaluationRoleTopicWeight {
        params: {
            evaluationRoleId: string;
        };
        body: {
            topicIds: string[];
        };
    }
    export interface PerformEvaluation {
        params: {
            evaluationId: string;
        };
        body: {
            topicScores: {
                topicId: string;
                score: string | null;
                note?: string | null;
            }[];
        };
    }
    export interface requestEvaluationUpdate {
        params: {
            id: string;
        };
        body: {
            updateReason?: string;
        };
    }
    export interface approveRejectEvaluationUpdate {
        params: {
            id: string;
            status: EvaluationStatus.UPDATE_APPROVED | EvaluationStatus.SUBMITTED;
        };
    }
    export interface updateTopicScore {
        params: {
            topicId: string;
            evaluationId: string;
        };
        body: {
            score: EvaluationScoreLabel | null;
            note?: string | null;
        };
    }
    export interface CreateMedicalLeaveRequest {
        userSub: string;
        startDate: string;
        endDate?: string;
        description?: string;
        status: LeaveRequestStatus;
        files: File[];
        accessToken: string | null;
        idToken: string | null;
    }
    export interface MedicalLeaveRequestsForUser {
        query: {
            page?: number;
            size?: number;
            sort?: string;
            direction?: string;
        };
    }
    export interface DeleteMedicalLeaveRequest {
        params: {
            id: string;
        };
    }
    export interface UpcomingMedicalLeaveRequestsForUser {
        params: {
            userSub: string;
        };
    }
    export interface EditMedicalLeaveRequest {
        id: string;
        startDate?: string;
        endDate?: string | null;
        description?: string;
        files: File[];
        accessToken: string | null;
        idToken: string | null;
    }
    export interface HistoryMedicalLeaveRequestsForUser {
        params: {
            userSub: string;
        };
        query: {
            page?: number;
            size?: number;
            sort?: string;
            direction?: string;
        };
    }
    export interface Approve {
        params: {
            id: string;
        };
    }
    export interface Reject {
        params: {
            id: string;
        };
        body: {
            rejectReason?: string;
        };
    }
    export interface GetAllByOrganization {
        body: {
            userSubs?: string[];
            status?: LeaveRequestStatus[];
        };
    }
    export interface EndRequest {
        params: {
            id: string;
            endDate: string;
        };
    }
}

export namespace UserApiResponse {
    export interface UsersBulk {
        body: PublicUserDto[];
    }

    export interface PaginatedUsers {
        body: PaginatedPublicUserDto;
    }

    export interface PublicUser {
        body: PublicUserDto[];
    }

    export interface ExcludedUsers {
        body: UserProfileWithRoleDto[];
    }

    export interface UserData {
        body: UserDto;
    }

    export interface GetAffectedMembersOfPolicy {
        body: PublicUserWithoutPoliciesAndRoleDto[];
    }

    export interface ChangePolicyInitialAmount {
        body: PolicyDto;
    }

    export interface GetUsersByRole {
        body: PublicUserDto[];
    }

    export interface GetOrganizationMembersCount {
        body: {
            totalMembers: number;
            joinedAtCount: number;
        };
    }

    export interface GetColleaguesBirthdays {
        body: PublicUserWithBirthday[];
    }

    export interface GetAnniversaries {
        body: PublicUserWithAnniversary[];
    }

    export interface GetGoogleIntegrationInfo {
        body: GoogleIntegration;
    }

    export interface GetUserDocuments {
        body: PaginatedUserDocumentsDto;
    }
    export interface GetUserCategories {
        body: { category: Category; documentCount: number }[];
    }
    export interface GetUserCategory {
        body: Category;
    }

    export interface GetUserDocument {
        body: UserDocumentDto | UserDocumentPresignedDto;
    }

    export interface UploadUserDocument {
        body: UserDocumentDto;
    }

    export interface getUserCurrentScopes {
        body: string[];
    }
    export interface GetActivityLogResponse {
        body: ActivityLogResponseDto;
    }

    export interface RecalculatePoliciesAmountForUser {
        body: RecalculatePoliciesAmountForUserDTO;
    }
    export interface GetNotificationResponse {
        body: NotificationResponseDto;
    }

    export interface GetNotificationSettingsResponse {
        body: NotificationSettingsDto;
    }

    export interface EnableDisableNotification {
        body: {
            enabled: boolean;
        };
    }
    export interface SlackActiveResponse {
        body: {
            redirectUrl: string;
        };
    }
    export interface TeamsIntegrationAuthorizeUrlResponse {
        body: {
            url: string;
        };
    }
    export interface IntegrationsStatusResponse {
        body: {
            slack: boolean;
            google: boolean;
            outlook: boolean;
            teams: boolean;
        };
    }
    export interface TeamsIntegrationStatusResponse {
        body: {
            isActive: boolean;
            teamName?: string;
            channelsProvisioned?: boolean;
            isHealthy?: boolean;
            status?: string;
        };
    }
    export interface TeamsIntegrationTeamDto {
        id: string;
        displayName?: string;
        name?: string;
    }
    export interface TeamsIntegrationTeamsResponse {
        body: TeamsIntegrationTeamDto[] | { teams: TeamsIntegrationTeamDto[] };
    }
    export interface TeamsIntegrationBotInstallResponse {
        body: {
            status?: string;
            isHealthy?: boolean;
        };
    }
    export interface SlackIntegrationStatusResponse {
        body: boolean;
    }
    export interface UserSlackInfoResponse {
        body: {
            id: string;
            isActive: boolean;
            user: {
                id: string;
                isActive: boolean;
            } | null;
            channels: Array<{
                id: string;
                name: string;
                type: string;
                isActive: boolean;
            }>;
        };
    }

    export interface GetEvaluationById {
        body: Evaluation;
    }
    export interface GetAllEvaluations {
        body: { data: Evaluation[]; total: number };
    }
    export interface RequestEvaluation {
        body: {
            id: string;
            evaluatorUserSub: string;
            evaluateeUserSubs: string[];
            evaluatorToEvaluateeRoleId: string;
            dueDate: string;
            isAnonymous: boolean;
            status: string;
            topicIds: string[];
        };
    }

    export interface GetTopicsByCategoryName {
        body: {
            id: string;
            name: string;
            description: string;
            neutralScoreDescription: string;
            positiveScoreDescription: string;
            negativeScoreDescription: string;
            topicCategoryId: string;
            setName?: string;
        }[];
    }

    export interface GetEvaluationCategories {
        body: {
            id: string;
            name: string;
            description: string;
        }[];
    }

    export interface GetEvaluationRoles {
        body: {
            id: string;
            direction: string;
        }[];
    }

    export interface GetEvaluationRoleTopicWeight {
        body: {
            topicId: string;
            weight: number;
        }[];
    }

    export interface PerformEvaluation {
        body: {
            topicScores: {
                topicId: string;
                score: string;
                note?: string;
            }[];
        };
    }

    export interface CreateMedicalLeaveRequest {
        body: MedicalLeaveRequestBaseResponseDto;
    }

    export interface MedicalLeaveRequestsPageable {
        body: Page<MedicalLeaveRequestBaseResponseDto>;
    }

    export interface EditMedicalLeaveRequest {
        body: MedicalLeaveRequestBaseResponseDto;
    }
    export interface MedicalLeaveRequestsNonPagable {
        body: MedicalLeaveRequestBaseResponseDto[];
    }
    export interface GetAllByOrganization {
        body: MedicalLeaveRequestBaseResponseDto[];
    }
}

export class UserApi {
    constructor(private readonly apiBase: ApiBaseClass) {}

    public patchUser(rest: IServiceHttp<UserApiRequest.UserData, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/user',
        });
    }

    public patchUserById(rest: IServiceHttp<UserApiRequest.UserDataById, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/user/userSub/:userSub',
        });
    }

    public getUsersByUserSubs(rest: IServiceHttp<UserApiRequest.UsersBulk, UserApiResponse.UsersBulk>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user.getBulk',
        });
    }

    public joinOrganzation(rest: IServiceHttp<UserApiRequest.JoinOrganzation, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/-joinOrganization/:joinToken',
        });
    }

    public getAllUsersFromOrganizationPaginated(rest: IServiceHttp<UserApiRequest.UsersBulk, UserApiResponse.PaginatedUsers>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/organization/:organizationId/users-paginated',
        });
    }

    public getAllUsersFromOrganization(rest: IServiceHttp<UserApiRequest.UsersBulk, UserApiResponse.PublicUser>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/organization/:organizationId/users',
        });
    }

    public getUsersByRole(rest: IServiceHttp<UserApiRequest.GetUsersByRole, UserApiResponse.GetUsersByRole>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/-withRoles',
        });
    }

    public changePolicyInitialAmount(rest: IServiceHttp<UserApiRequest.ChangePolicyInitialAmount, UserApiResponse.ChangePolicyInitialAmount>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/:userSub/policy/:leaveType/change-initial-amount',
        });
    }

    public changeUserRole(rest: IServiceHttp<UserApiRequest.RoleData, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user.changeRole',
        });
    }

    public deleteUser(rest: IServiceHttp<UserApiRequest.RemoveUserFromOrganization, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/userSub/:userSub/-removeFromOrganization',
        });
    }

    public getUser(rest: IServiceHttp<UserApiRequest.User, UserApiResponse.UserData>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/user/:userSub',
        });
    }

    public getAffectedMembersOfPolicy(rest: IServiceHttp<UserApiRequest.GetAffectedMembersOfPolicy, UserApiResponse.GetAffectedMembersOfPolicy>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/policy.getAffectedMembers',
        });
    }

    public getOrganizationMembersCount(rest: IServiceHttp<UserApiRequest.GetOrganizationMembersCount, UserApiResponse.GetOrganizationMembersCount>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/user/organization-members-count/joined-at',
        });
    }

    public getColleaguesBirthdays(rest: IServiceHttp<UserApiRequest.GetColleaguesBirthdays, UserApiResponse.GetColleaguesBirthdays>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/user/colleagues-birthdays',
        });
    }

    public getAnniversaries(rest: IServiceHttp<UserApiRequest.GetAnniversaries, UserApiResponse.GetAnniversaries>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/user/anniversaries',
        });
    }

    public connectGoogleIntegration(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/google-integration/-connect',
        });
    }

    public googleIntegrationAuth(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/google-integration/-auth',
        });
    }

    public googleCalendarEnable(rest: IServiceHttp<UserApiRequest.GoogleCalendarEnable, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/google-integration/google-calendar/-enable',
        });
    }

    public googleCalendarDisable(rest: IServiceHttp<UserApiRequest.GoogleCalendarDisable, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/google-integration/google-calendar/-disable',
        });
    }

    public getGoogleIntegration(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.GetGoogleIntegrationInfo>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/google-integration',
        });
    }

    public gmailAutoReplyEnable(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/google-integration/gmail/-enableAutoReply',
        });
    }

    public gmailAutoReplyDisable(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/google-integration/gmail/-disableAutoReply',
        });
    }

    public disableGoogleIntegration(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/google-integration/-disconnect',
        });
    }

    public updateGoogleIntegration(rest: IServiceHttp<UserApiRequest.GoogleIntegrationPatch, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/google-integration',
        });
    }

    public getUserDocuments(rest: IServiceHttp<UserApiRequest.GetUserDocuments, UserApiResponse.GetUserDocuments>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/user-document/userSub/:userSub',
        });
    }

    public getUserDocument(rest: IServiceHttp<UserApiRequest.GetUserDocument, UserApiResponse.GetUserDocument>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/user-document/id/:id',
        });
    }

    public getDocumentCategories(rest: IServiceHttp<{}, UserApiResponse.GetUserCategories>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/categories',
        });
    }

    public getDocumentCategoryById(rest: IServiceHttp<UserApiRequest.DeleteUserDocument, UserApiResponse.GetUserCategory>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/categories/id/:id',
        });
    }

    public createDocumentCategory(rest: IServiceHttp<UserApiRequest.CreateCategory, UserApiResponse.GetUserCategory>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/categories',
        });
    }

    public editDocumentCategory(rest: IServiceHttp<UserApiRequest.PatchCategory, UserApiResponse.GetUserCategory>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/categories/id/:id',
        });
    }

    public changeBranch(rest: IServiceHttp<UserApiRequest.ChangeBranch, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/userSub/:userSub/-changeBranch',
        });
    }

    public deleteUserDocument(rest: IServiceHttp<UserApiRequest.DeleteUserDocument, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'usr/v1/user-document/id/:id',
        });
    }

    public deleteDocumentCategory(rest: IServiceHttp<UserApiRequest.DeleteUserDocument, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'usr/v1/categories/id/:id',
        });
    }

    public async uploadUserDocument(data: UserApiRequest.UploadUserDocument): Promise<UserApiResponse.UploadUserDocument> {
        const headers = new Headers();
        headers.append('X-Id-Token', data.idToken ?? '');
        headers.append('Authorization', `Bearer ${data.accessToken ?? ''}`);
        const formData = new FormData();
        const encodedFileName = encodeURIComponent(data.file.name);
        formData.append('document', data.file, encodedFileName);
        formData.append('name', data.name);
        if (data.description) formData.append('description', data.description);
        return fetch(`${AppConfig.serverUrlBase}/usr/v1/user-document/userSub/${data.userSub}?categoryId=${encodeURIComponent(data.categoryId)}`, {
            method: 'POST',
            headers: headers,
            body: formData,
        }).then(res => {
            if (!res.ok) throw new Error('Network response not ok');
            return res.json();
        });
    }

    public editUserDocument(rest: IServiceHttp<UserApiRequest.PatchDocument, UserApiResponse.GetUserDocument>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/user-document/userSub/:userSub',
        });
    }

    public getUserCurrentScopes(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.getUserCurrentScopes>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/google-integration/-userCurrentScopes',
        });
    }

    public getActivityLogs(rest: IServiceHttp<UserApiRequest.GetActivityLogRequest, UserApiResponse.GetActivityLogResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/activity-log/user/userSub/:userSub',
        });
    }

    public recalculatePoliciesAmountForUser(rest: IServiceHttp<UserApiRequest.RecalculatePoliciesAmountForUser, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/user/-recalculateDaysBasedOnContractEndDate',
        });
    }

    public getNotificationsByUser(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.GetNotificationResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/notifications',
        });
    }

    public markNotificationAsRead(rest: IServiceHttp<UserApiRequest.GetNotificationRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/notifications/id/:id/-markAsRead',
        });
    }

    public markAllNotificationsAsRead(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/notifications/-markAllAsRead',
        });
    }

    public getNotificationSettings(rest: IServiceHttp<UserApiRequest.GetNotificationSettings, UserApiResponse.GetNotificationSettingsResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/notification-settings',
        });
    }

    public disableOrEnableNotification(rest: IServiceHttp<UserApiRequest.EnableDisableNotification, UserApiResponse.EnableDisableNotification>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/notification-settings/name/:name',
        });
    }
    public activateSlackIntegration(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.SlackActiveResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/slack-integration/authorize-url',
        });
    }
    public getIntegrationsStatus(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.IntegrationsStatusResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/integrations/status',
        });
    }
    public initialSlackDeactivation(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'usr/v1/slack-integration/deactivate',
        });
    }
    public individualSlackDeactivation(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'usr/v1/slack-integration/individual-deactivate',
        });
    }
    public getUserSlackInfo(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.UserSlackInfoResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/slack-integration',
        });
    }
    public patchUsersSlackChannels(rest: IServiceHttp<UserApiRequest.PatchSlackChannels, UserApiResponse.UserSlackInfoResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/slack-integration/channels',
        });
    }
    public getSlackIntegrationStatus(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.SlackIntegrationStatusResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/slack-integration/status',
        });
    }
    public getTeamsIntegrationStatus(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.TeamsIntegrationStatusResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/teams-integration/status',
        });
    }
    public getTeamsIntegrationAuthorizeUrl(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.TeamsIntegrationAuthorizeUrlResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/teams-integration/authorize-url',
        });
    }
    public getTeamsIntegrationTeams(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.TeamsIntegrationTeamsResponse>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/teams-integration/teams',
        });
    }
    public selectTeamsIntegrationTeam(rest: IServiceHttp<UserApiRequest.TeamsIntegrationSelectTeam, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/teams-integration/select-team',
        });
    }
    public provisionTeamsIntegrationChannels(rest: IServiceHttp<UserApiRequest.TeamsIntegrationProvisionChannels, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/teams-integration/provision-channels',
        });
    }
    public installTeamsIntegrationBot(
        rest: IServiceHttp<UserApiRequest.TeamsIntegrationBotInstall, UserApiResponse.TeamsIntegrationBotInstallResponse>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/teams-integration/bot-install',
        });
    }
    public sendTeamsIntegrationTestMessage(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/teams-integration/test-message',
        });
    }
    public deactivateTeamsIntegration(rest: IServiceHttp<UserApiRequest.EmptyRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'usr/v1/teams-integration/deactivate',
        });
    }
    public teamsIntegrationCallback(rest: IServiceHttp<UserApiRequest.TeamsIntegrationCallback, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/teams-integration/oauth/callback',
        });
    }
    public getEvaluationById(rest: IServiceHttp<UserApiRequest.GetEvaluationById, UserApiResponse.GetEvaluationById>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/evaluation/id/:id',
        });
    }
    public getAllEvaluations(rest: IServiceHttp<UserApiRequest.GetAllEvaluations, UserApiResponse.GetAllEvaluations>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/evaluation/getAll',
        });
    }

    public getEvaluationCategories(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.GetEvaluationCategories>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/topic-categories',
        });
    }

    public getTopicsByCategoryName(rest: IServiceHttp<UserApiRequest.GetTopicsByCategoryName, UserApiResponse.GetTopicsByCategoryName>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/topics',
        });
    }

    public getTopics(rest: IServiceHttp<UserApiRequest.GetTopicsByCategoryName, UserApiResponse.GetTopicsByCategoryName>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/topics',
        });
    }

    public requestEvaluation(rest: IServiceHttp<UserApiRequest.RequestEvaluation, UserApiResponse.RequestEvaluation>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/evaluation',
        });
    }

    public getEvaluationRoles(rest: IServiceHttp<UserApiRequest.EmptyRequest, UserApiResponse.GetEvaluationRoles>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/evaluation-role',
        });
    }

    public getEvaluationRoleTopicWeight(
        rest: IServiceHttp<UserApiRequest.GetEvaluationRoleTopicWeight, UserApiResponse.GetEvaluationRoleTopicWeight>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/evaluation-role-topic-weight/evaluationRoleId/:evaluationRoleId/-topicWeights',
        });
    }

    public performEvaluation(rest: IServiceHttp<UserApiRequest.PerformEvaluation, UserApiResponse.PerformEvaluation>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/evaluation/id/:evaluationId/-perform',
        });
    }

    public requestEvaluationUpdate(rest: IServiceHttp<UserApiRequest.requestEvaluationUpdate, UserApiResponse.RequestEvaluation>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/evaluation/id/:id/-requestUpdate',
        });
    }
    public approveRejectEvaluationUpdate(rest: IServiceHttp<UserApiRequest.approveRejectEvaluationUpdate, UserApiResponse.RequestEvaluation>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/evaluation/id/:id/status/:status/-approveOrRejectUpdate',
        });
    }
    public updateTopicScore(rest: IServiceHttp<UserApiRequest.updateTopicScore, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/topic-score/topicId/:topicId/evaluationId/:evaluationId',
        });
    }
    public async createMedicalLeaveRequest(data: UserApiRequest.CreateMedicalLeaveRequest): Promise<UserApiResponse.CreateMedicalLeaveRequest> {
        const headers = new Headers();
        headers.append('X-Id-Token', data.idToken ?? '');
        headers.append('Authorization', `Bearer ${data.accessToken ?? ''}`);
        const formData = new FormData();
        formData.append('userSub', data.userSub);
        formData.append('startDate', data.startDate);
        formData.append('status', data.status);

        if (data.endDate) formData.append('endDate', data.endDate);
        if (data.description) formData.append('description', data.description);
        if (data.files && data.files.length > 0) {
            data.files.forEach(file => {
                formData.append('documents', file);
            });
        }

        return fetch(`${AppConfig.serverUrlBase}/usr/v1/medical-leave-request`, {
            method: 'POST',
            headers: headers,
            body: formData,
        }).then(res => {
            if (!res.ok) throw new Error('Network response not ok');
            return res.json();
        });
    }
    public medicalLeaveRequestsForUser(rest: IServiceHttp<UserApiRequest.MedicalLeaveRequestsForUser, UserApiResponse.MedicalLeaveRequestsPageable>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/medical-leave-request',
        });
    }

    public deleteMedicalLeaveRequest(rest: IServiceHttp<UserApiRequest.DeleteMedicalLeaveRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'usr/v1/medical-leave-request/id/:id',
        });
    }

    public upcomingMedicalLeaveRequestsForUser(
        rest: IServiceHttp<UserApiRequest.UpcomingMedicalLeaveRequestsForUser, UserApiResponse.MedicalLeaveRequestsNonPagable>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/medical-leave-request/userSub/:userSub/upcoming',
        });
    }

    public async editMedicalLeaveRequest(data: UserApiRequest.EditMedicalLeaveRequest): Promise<UserApiResponse.EditMedicalLeaveRequest> {
        try {
            const headers = new Headers();
            headers.append('X-Id-Token', data.idToken ?? '');
            headers.append('Authorization', `Bearer ${data.accessToken ?? ''}`);

            if (!data.files || data.files.length === 0) {
                headers.append('Content-Type', 'application/json');

                const body: any = {};
                if (data.description !== undefined) body.description = data.description;
                if (data.startDate !== undefined) body.startDate = data.startDate;
                if (data.endDate !== undefined) body.endDate = data.endDate;

                const response = await fetch(`${AppConfig.serverUrlBase}/usr/v1/medical-leave-request/id/${data.id}`, {
                    method: 'PATCH',
                    headers,
                    body: JSON.stringify(body),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Network response not ok: ${errorText}`);
                }

                const result: UserApiResponse.EditMedicalLeaveRequest = await response.json();
                return result;
            }

            const formData = new FormData();

            if (data.description) formData.append('description', data.description);
            if (data.startDate) formData.append('startDate', data.startDate);
            if (data.endDate) formData.append('endDate', data.endDate);

            if (data.files && data.files.length > 0) {
                for (const file of data.files) {
                    formData.append('documents', file);
                }
            }

            const response = await fetch(`${AppConfig.serverUrlBase}/usr/v1/medical-leave-request/id/${data.id}`, {
                method: 'PATCH',
                headers,
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Network response not ok: ${errorText}`);
            }

            const result: UserApiResponse.EditMedicalLeaveRequest = await response.json();
            return result;
        } catch (error) {
            console.error('Error editing medical leave request:', error);
            throw error;
        }
    }

    public historyMedicalLeaveRequestsForUser(
        rest: IServiceHttp<UserApiRequest.HistoryMedicalLeaveRequestsForUser, UserApiResponse.MedicalLeaveRequestsPageable>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'usr/v1/medical-leave-request/userSub/:userSub/history',
        });
    }

    public approve(rest: IServiceHttp<UserApiRequest.Approve, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/medical-leave-request/id/:id/-approve',
        });
    }

    public reject(rest: IServiceHttp<UserApiRequest.Reject, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/medical-leave-request/id/:id/-reject',
        });
    }
    public getAllByOrganization(rest: IServiceHttp<UserApiRequest.GetAllByOrganization, UserApiResponse.GetAllByOrganization>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'usr/v1/medical-leave-request/organization',
        });
    }

    public endRequest(rest: IServiceHttp<UserApiRequest.EndRequest, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'usr/v1/medical-leave-request/id/:id/endDate/:endDate',
        });
    }
}

diContainer.bindAsDynamic(UserApi, (c: any) => new UserApi(c.get(ApiBaseClass)));
